"use client"

/* ===================== Services ===================== */

/* ===================== Solutions ===================== */

/* ===================== Work ===================== */

/* ===================== About ===================== */

/* ===================== Insights ===================== */

/* ===================== Legal & 404 ===================== */

import { useState } from "react"
import { Link, useT, useRouter } from "../lib/router"
import { services, coreServices, getService } from "../content/services"
import {
  solutions,
  getSolution,
  caseStudies,
  getCase,
  insightCategories,
  publishedArticles,
  articles,
} from "../content/catalog"
import {
  Button,
  CheckList,
  Container,
  CTASection,
  EmptyState,
  FAQList,
  Icon,
  LinkCard,
  PageHeader,
  Pill,
  Section,
  SectionHeading,
  SITE,
} from "../components/ui"
import { journey } from "./Home"

const method = [
  {
    en: "Understand",
    ar: "نفهم",
    d: { en: "Needs and bottlenecks.", ar: "الاحتياج ونقاط التعطل." },
  },
  {
    en: "Design",
    ar: "نصمم",
    d: { en: "Experience and workflow.", ar: "التجربة ومسار العمل." },
  },
  {
    en: "Build",
    ar: "نبني",
    d: { en: "The product and solution.", ar: "المنتج والحل المناسب." },
  },
  {
    en: "Connect",
    ar: "نربط",
    d: { en: "Tools and ownership.", ar: "الأدوات والمسؤوليات." },
  },
  {
    en: "Improve",
    ar: "نحسّن",
    d: { en: "Through real use.", ar: "بناءً على الاستخدام." },
  },
]

function Methodology() {
  const t = useT()
  return (
    <ol className="grid gap-px overflow-hidden rounded-r16 border border-line bg-line sm:grid-cols-5">
      {method.map((m, i) => (
        <li key={m.en} className="bg-white p-6">
          <span className="text-sm font-medium text-blue">0{i + 1}</span>
          <p className="mt-3 text-lg font-medium text-ink">{t(m)}</p>
          <p className="mt-1 text-base text-slate">{t(m.d)}</p>
        </li>
      ))}
    </ol>
  )
}

export function ServicesIndex() {
  const t = useT()
  const support = services.find((s) => s.tier === "supporting")!
  return (
    <>
      <PageHeader
        crumbs={[{ label: t({ en: "Services", ar: "الخدمات" }) }]}
        eyebrow={t({ en: "Services", ar: "الخدمات" })}
        title={t({
          en: "Reach your customers and make daily work easier.",
          ar: "تواصل مع عملائك، وسهّل العمل اليومي.",
        })}
        lead={t({
          en: "Choose the services that fit your goals, from your website to managing customers and training your team.",
          ar: "اختر الخدمات التي تناسب أهدافك، من موقعك الإلكتروني إلى إدارة عملائك وتدريب فريقك.",
        })}
      />
      <Section>
        <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {coreServices.map((s, i) => (
            <LinkCard
              key={s.slug}
              to={`/services/${s.slug}`}
              icon={s.icon}
              eyebrow={`0${i + 1}`}
              title={t(s.title)}
              body={t(s.summary)}
            />
          ))}
        </div>
        <div className="mt-5">
          <Link
            to={`/services/${support.slug}`}
            className="group flex flex-col gap-6 rounded-r16 border border-line bg-mist p-7 hover:border-blue md:flex-row md:items-center"
          >
            <span className="inline-flex size-12 shrink-0 items-center justify-center rounded-r8 bg-white text-blue">
              <Icon name={support.icon} />
            </span>
            <div className="flex-1">
              <p className="text-sm font-medium text-slate">
                {t({ en: "Supporting service", ar: "خدمة داعمة" })}
              </p>
              <h3 className="mt-1 text-xl font-medium text-ink">
                {t(support.title)}
              </h3>
              <p className="mt-2 text-base text-slate">{t(support.summary)}</p>
            </div>
            <Icon name="arrow" flip className="size-6 text-blue" />
          </Link>
        </div>
      </Section>
      <Section tone="mist">
        <SectionHeading
          eyebrow={t({ en: "How we work", ar: "منهج العمل" })}
          title={t({
            en: "A clear path from need to delivery.",
            ar: "من احتياج واضح إلى نظام يعمل.",
          })}
          lead={t({
            en: "A shared process with visible checkpoints, used on every engagement.",
            ar: "مسار مفهوم ونقاط مراجعة واضحة في كل مشروع.",
          })}
        />
        <div className="mt-12">
          <Methodology />
        </div>
      </Section>
      <CTASection />
    </>
  )
}

export function ServiceDetail({ slug }: { slug: string }) {
  const t = useT()
  const s = getService(slug)!
  const related = s.relatedSolutions.map(getSolution).filter(Boolean)
  const relatedWork = caseStudies.filter(
    (c) => c.published && c.services.includes(s.slug),
  )
  return (
    <>
      <PageHeader
        crumbs={[
          { label: t({ en: "Services", ar: "الخدمات" }), to: "/services" },
          { label: t(s.title) },
        ]}
        icon={s.icon}
        title={t(s.title)}
        lead={t(s.heroLead)}
      >
        <Button to={`/start-project?service=${s.slug}`}>
          {t({ en: "Discuss your project", ar: "ناقش مشروعك" })}
        </Button>
        <Button to="/contact" variant="secondary">
          {t({ en: "Book a consultation", ar: "احجز استشارة" })}
        </Button>
      </PageHeader>

      <Section>
        <div className="grid gap-12 lg:grid-cols-2 lg:gap-20">
          <div>
            <p className="text-sm font-medium uppercase tracking-[0.12em] text-slate rtl:tracking-normal">
              {t({ en: "The problem", ar: "المشكلة" })}
            </p>
            <p className="mt-5 text-xl leading-8 text-ink md:text-2xl md:leading-10">
              {t(s.problem)}
            </p>
          </div>
          <div className="lg:border-s lg:border-line lg:ps-20">
            <p className="text-sm font-medium uppercase tracking-[0.12em] text-blue rtl:tracking-normal">
              {t({ en: "Our approach", ar: "نهجنا" })}
            </p>
            <p className="mt-5 text-lg leading-8 text-slate">{t(s.approach)}</p>
          </div>
        </div>
      </Section>

      <Section tone="mist">
        <SectionHeading
          title={t({
            en: "What you get",
            ar: "ما تحصل عليه",
          })}
        />
        <div className="mt-10 rounded-r24 bg-white p-8 md:p-12">
          <CheckList items={s.capabilities.map(t)} />
        </div>
      </Section>

      <Section>
        <div className="max-w-4xl">
          <SectionHeading
            title={t({ en: "Typical use cases", ar: "حالات استخدام شائعة" })}
          />
          <ul className="mt-10 space-y-4">
            {s.useCases.map((u, i) => (
              <li key={i} className="flex gap-5 rounded-r16 border border-line p-6">
                <span className="text-sm font-medium text-blue">0{i + 1}</span>
                <span className="text-base leading-7 text-ink">{t(u)}</span>
              </li>
            ))}
          </ul>
        </div>
      </Section>

      <Section tone="mist">
        <SectionHeading
          title={t({ en: "How we work with you", ar: "كيف نعمل معك" })}
        />
        <div className="mt-10">
          <Methodology />
        </div>
      </Section>

      {(related.length > 0 || relatedWork.length > 0) && (
        <Section>
          <SectionHeading
            title={t({
              en: "Related solutions and work",
              ar: "حلول وأعمال ذات صلة",
            })}
          />
          <div className="mt-10 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
            {related.map((r) => (
              <LinkCard
                key={r!.slug}
                to={`/solutions/${r!.slug}`}
                eyebrow={t({ en: "Solution", ar: "حل" })}
                title={t(r!.title)}
                body={t(r!.summary)}
              />
            ))}
            {relatedWork.map((c) => (
              <LinkCard
                key={c.slug}
                to={`/work/${c.slug}`}
                eyebrow={t({ en: "Work", ar: "عمل" })}
                title={t(c.title)}
                body={t(c.solution)}
              />
            ))}
          </div>
        </Section>
      )}

      {s.faqs.length > 0 && (
        <Section tone={related.length ? "mist" : "white"}>
          <div className="grid gap-12 lg:grid-cols-12">
            <div className="lg:col-span-4">
              <SectionHeading
                title={t({ en: "Questions we hear", ar: "أسئلة متكررة" })}
              />
            </div>
            <div className="lg:col-span-8">
              <FAQList items={s.faqs.map((f) => ({ q: t(f.q), a: t(f.a) }))} />
            </div>
          </div>
        </Section>
      )}
      <CTASection />
    </>
  )
}

export function SolutionsIndex() {
  const t = useT()
  return (
    <>
      <PageHeader
        crumbs={[{ label: t({ en: "Solutions", ar: "الحلول" }) }]}
        eyebrow={t({ en: "Solutions", ar: "الحلول" })}
        title={t({
          en: "Built around your business.",
          ar: "شريك يفهم طريقة عملك.",
        })}
        lead={t({
          en: "We organize sales, customers and daily work around the needs of your industry, with clear features agreed for your project.",
          ar: "ننظم المبيعات والعملاء والعمل اليومي حسب احتياجات قطاعك، بمزايا واضحة نتفق عليها لمشروعك.",
        })}
      />
      <Section>
        <div className="grid gap-5 md:grid-cols-2">
          {solutions.map((s) => (
            <LinkCard
              key={s.slug}
              to={`/solutions/${s.slug}`}
              title={t(s.title)}
              body={t(s.summary)}
              className="md:p-9"
            />
          ))}
        </div>
      </Section>
      <Section tone="mist">
        <SectionHeading
          title={t({
            en: "Your industry is not listed?",
            ar: "قطاعك غير مذكور؟",
          })}
          lead={t({
            en: "Tell us about your customers and daily work, and we will discuss a solution that fits your needs.",
            ar: "أخبرنا عن عملائك والعمل اليومي لفريقك، لنناقش حلًا يناسب احتياجاتك.",
          })}
        />
        <div className="mt-8">
          <Button to="/contact">
            {t({ en: "Talk to us", ar: "تحدث إلينا" })}
          </Button>
        </div>
      </Section>
      <CTASection />
    </>
  )
}

export function SolutionDetail({ slug }: { slug: string }) {
  const t = useT()
  const s = getSolution(slug)!
  return (
    <>
      <PageHeader
        crumbs={[
          { label: t({ en: "Solutions", ar: "الحلول" }), to: "/solutions" },
          { label: t(s.title) },
        ]}
        eyebrow={t({ en: "Industry solution", ar: "حل قطاعي" })}
        title={t(s.title)}
        lead={t(s.summary)}
      >
        <Button to={`/start-project?solution=${s.slug}`}>
          {t({ en: "Discuss your workflow", ar: "ناقش مسار عملك" })}
        </Button>
      </PageHeader>
      <Section>
        <div className="max-w-3xl">
          <p className="text-sm font-medium uppercase tracking-[0.12em] text-slate rtl:tracking-normal">
            {t({ en: "The challenge", ar: "التحدي" })}
          </p>
          <p className="mt-5 text-xl leading-8 text-ink md:text-2xl md:leading-10">
            {t(s.challenge)}
          </p>
        </div>
      </Section>
      <Section tone="mist">
        <div className="flex flex-col justify-between gap-6 lg:flex-row lg:items-end">
          <SectionHeading
            title={t({
              en: "What the solution includes",
              ar: "ما يتضمنه الحل",
            })}
            lead={t({
              en: "We agree the features your team needs and how they will help you manage daily work.",
              ar: "نتفق على المزايا التي يحتاجها فريقك وكيف تساعدك على إدارة العمل اليومي.",
            })}
          />
        </div>
        <div className="mt-10 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {s.capabilities.map((c) => (
            <div
              key={c.title.en}
              className="flex flex-col rounded-r16 border border-line bg-white p-7"
            >
              <h3 className="text-lg font-medium text-ink">
                {t(c.title)}
              </h3>
              <p className="mt-2 text-base leading-7 text-slate">{t(c.body)}</p>
            </div>
          ))}
        </div>
      </Section>
      <Section>
        <SectionHeading
          title={t({ en: "Services involved", ar: "الخدمات المرتبطة" })}
        />
        <div className="mt-10 grid gap-5 md:grid-cols-3">
          {s.services
            .map(getService)
            .map(
              (x) =>
                x && (
                  <LinkCard
                    key={x.slug}
                    to={`/services/${x.slug}`}
                    icon={x.icon}
                    title={t(x.title)}
                    body={t(x.summary)}
                  />
                ),
            )}
        </div>
      </Section>
      <CTASection />
    </>
  )
}

export function WorkIndex() {
  const t = useT()
  const list = caseStudies.filter((c) => c.published)
  return (
    <>
      <PageHeader
        crumbs={[{ label: t({ en: "Our Work", ar: "أعمالنا" }) }]}
        eyebrow={t({ en: "Our Work", ar: "أعمالنا" })}
        title={t({ en: "Our Work", ar: "أعمالنا" })}
        lead={list.length ? t({
          en: "Explore our projects, the challenges behind them and the solutions we built.",
          ar: "تعرّف إلى مشاريعنا والتحديات التي عالجتها والحلول التي بنيناها.",
        }) : t({
          en: "Our projects and case studies will be added here later.",
          ar: "سنضيف هنا مشاريعنا ودراسات أعمالنا لاحقًا.",
        })}
      />
      <Section>
        {list.length ? (
          <div className="grid gap-5 lg:grid-cols-3">
            {list.map((c) => (
              <LinkCard
                key={c.slug}
                to={`/work/${c.slug}`}
                eyebrow={t(c.industry)}
                title={t(c.title)}
                body={t(c.challenge)}
              />
            ))}
          </div>
        ) : (
          <EmptyState
            title={t({
              en: "No projects published yet",
              ar: "لا توجد أعمال منشورة حاليًا",
            })}
            body={t({
              en: "Explore our services or contact us to discuss your project.",
              ar: "يمكنك استكشاف خدماتنا أو التواصل معنا لمناقشة مشروعك.",
            })}
          />
        )}
      </Section>
      <CTASection />
    </>
  )
}

export function CaseDetail({ slug }: { slug: string }) {
  const t = useT()
  const c = getCase(slug)!
  return (
    <>
      <PageHeader
        crumbs={[
          { label: t({ en: "Our Work", ar: "أعمالنا" }), to: "/work" },
          { label: t(c.title) },
        ]}
        eyebrow={t(c.industry)}
        title={t(c.title)}
      >
        {c.disclosure === "anonymized" && (
          <Pill tone="line">
            {t({
              en: "Client details withheld pending approval",
              ar: "تفاصيل العميل محجوبة حتى الموافقة",
            })}
          </Pill>
        )}
      </PageHeader>
      <Section>
        <div className="grid gap-14 lg:grid-cols-12">
          <div className="space-y-12 lg:col-span-8">
            <div>
              <h2 className="text-2xl font-medium text-ink">
                {t({ en: "Business challenge", ar: "تحدي العمل" })}
              </h2>
              <p className="mt-4 text-lg leading-8 text-slate">
                {t(c.challenge)}
              </p>
            </div>
            <div>
              <h2 className="text-2xl font-medium text-ink">
                {t({ en: "Engineering solution", ar: "الحل الهندسي" })}
              </h2>
              <p className="mt-4 text-lg leading-8 text-slate">
                {t(c.solution)}
              </p>
            </div>
            <div>
              <h2 className="text-2xl font-medium text-ink">
                {t({ en: "Outcome", ar: "النتيجة" })}
              </h2>
              {c.outcomes.length ? (
                <div className="mt-4">
                  <CheckList columns={1} items={c.outcomes.map(t)} />
                </div>
              ) : (
                <p className="mt-4 text-base text-slate">
                  {t({
                    en: "Verified results will be added once approved for publication.",
                    ar: "ستُضاف النتائج الموثقة بعد اعتمادها للنشر.",
                  })}
                </p>
              )}
            </div>
          </div>
          <aside className="space-y-8 rounded-r24 bg-mist p-8 lg:col-span-4 lg:self-start">
            <div>
              <h2 className="text-sm font-medium uppercase tracking-[0.12em] text-slate rtl:tracking-normal">
                {t({ en: "Scope", ar: "النطاق" })}
              </h2>
              <div className="mt-4">
                <CheckList columns={1} items={c.scope.map(t)} />
              </div>
            </div>
            <div>
              <h2 className="text-sm font-medium uppercase tracking-[0.12em] text-slate rtl:tracking-normal">
                {t({ en: "Related services", ar: "خدمات مرتبطة" })}
              </h2>
              <ul className="mt-4 space-y-2">
                {c.services.map(getService).map(
                  (s) =>
                    s && (
                      <li key={s.slug}>
                        <Link
                          to={`/services/${s.slug}`}
                          className="text-base font-medium text-blue hover:underline"
                        >
                          {t(s.title)}
                        </Link>
                      </li>
                    ),
                )}
              </ul>
            </div>
          </aside>
        </div>
      </Section>
      <CTASection />
    </>
  )
}

export function About() {
  const t = useT()
  const beliefs = [
    {
      title: { en: "Clarity", ar: "وضوح" },
      body: { en: "Know the next step.", ar: "نعرف الخطوة التالية." },
    },
    {
      title: { en: "Connection", ar: "ترابط" },
      body: { en: "Make tools work together.", ar: "كل أداة تكمل الأخرى." },
    },
    {
      title: { en: "Flow", ar: "انسيابية" },
      body: { en: "Make handoffs simpler.", ar: "انتقالات أقل تعقيدًا." },
    },
  ]
  const philosophy = [
  {
    "en": "Start with your goals and the problems your team faces.",
    "ar": "نبدأ بأهدافك والمشكلات التي يواجهها فريقك."
  },
  {
    "en": "Explain the work, the budget and the stages clearly.",
    "ar": "نشرح العمل والميزانية والمراحل بوضوح."
  },
  {
    "en": "Let you try and review the work as it develops.",
    "ar": "نتيح لك تجربة العمل ومراجعته أثناء التنفيذ."
  },
  {
    "en": "Make everyday tasks easier for customers and employees.",
    "ar": "نجعل المهام اليومية أسهل للعملاء والموظفين."
  },
  {
    "en": "Keep you involved in important decisions.",
    "ar": "نُبقيك مشاركًا في القرارات المهمة."
  },
  {
    "en": "Agree training and support so your team can keep using the result.",
    "ar": "نتفق على التدريب والدعم ليستمر فريقك في استخدام ما نسلمه."
  }
]
  return (
    <>
      <PageHeader
        crumbs={[{ label: t({ en: "About", ar: "عن أوركست" }) }]}
        eyebrow={t({ en: "Who we are", ar: "من نحن" })}
        title={t({
          en: "From moving parts to a clear whole.",
          ar: "ننسّق التعقيد، ونصنع الوضوح.",
        })}
        lead={t({
          en: "Orkest Digital Solutions helps businesses organize their work and build their digital presence. We are based in Cairo, Egypt. We work with businesses in Egypt, Saudi Arabia, the GCC and the wider Arab world — and with international clients where it fits.",
          ar: "أوركست لتكنولوجيا المعلومات تساعد الشركات على تنظيم أعمالها وبناء حضورها الرقمي. مقرنا القاهرة، مصر. نعمل مع الشركات في مصر والسعودية والخليج والعالم العربي، ومع عملاء دوليين حين يناسب ذلك.",
        })}
      />
      <Section>
        <div className="grid gap-5 md:grid-cols-2">
          <div className="rounded-r24 bg-blue p-10 text-white">
            <p className="text-sm font-medium uppercase tracking-[0.12em] text-white rtl:tracking-normal">
              {t({ en: "Our mission", ar: "رسالتنا" })}
            </p>
            <p className="mt-5 text-2xl font-medium leading-9">
              {t({
                en: "Help businesses reach customers, organize work and reduce repeated tasks with clear digital solutions.",
                ar: "مساعدة الشركات على الوصول إلى العملاء وتنظيم العمل وتقليل المهام المتكررة بحلول رقمية واضحة.",
              })}
            </p>
          </div>
          <div className="rounded-r24 bg-mist p-10">
            <p className="text-sm font-medium uppercase tracking-[0.12em] text-slate rtl:tracking-normal">
              {t({ en: "Our vision", ar: "رؤيتنا" })}
            </p>
            <p className="mt-5 text-2xl font-medium leading-9 text-ink">
              {t({
                en: "Businesses in our region reaching their customers and managing daily work with confidence.",
                ar: "شركات في منطقتنا تصل إلى عملائها وتدير عملها اليومي بثقة.",
              })}
            </p>
          </div>
        </div>
      </Section>
      <Section tone="mist">
        <SectionHeading
          eyebrow={t({ en: "What we believe", ar: "ما نؤمن به" })}
          title={t({
            en: "The idea is to make every part work together.",
            ar: "الفكرة أن تعمل الأجزاء معًا.",
          })}
        />
        <div className="mt-12 grid gap-5 md:grid-cols-3">
          {beliefs.map((b, i) => (
            <div key={b.title.en} className="rounded-r16 bg-white p-8">
              <span className="text-sm font-medium text-blue">0{i + 1}</span>
              <h3 className="mt-4 text-2xl font-medium text-ink">
                {t(b.title)}
              </h3>
              <p className="mt-2 text-base text-slate">{t(b.body)}</p>
            </div>
          ))}
        </div>
      </Section>
      <Section>
        <div className="grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <SectionHeading
              eyebrow={t({ en: "Our way of working", ar: "طريقة عملنا" })}
              title={t({
                en: "Clear goals, practical steps and regular review.",
                ar: "أهداف واضحة، وخطوات عملية، ومراجعة مستمرة.",
              })}
            />
          </div>
          <div className="lg:col-span-7">
            <CheckList columns={1} items={philosophy.map(t)} />
          </div>
        </div>
      </Section>
      <Section tone="mist">
        <SectionHeading
          eyebrow={t({ en: "How we work", ar: "كيف نعمل" })}
          title={t({
            en: "A shared process with visible checkpoints.",
            ar: "مسار مفهوم ونقاط مراجعة واضحة.",
          })}
        />
        <div className="mt-10">
          <Methodology />
        </div>
      </Section>
      <Section>
        <SectionHeading
          eyebrow={t({
            en: "Our approach to transformation",
            ar: "نهجنا في التحول",
          })}
          title={t({
            en: "A flexible journey, not a fixed package.",
            ar: "رحلة مرنة وليست باقة ثابتة.",
          })}
        />
        <ol className="mt-10 grid gap-5 md:grid-cols-5">
          {journey.map((j) => (
            <li key={j.n}>
              <Link
                to={j.to}
                className="block h-full rounded-r16 border border-line p-6 hover:border-blue"
              >
                <span className="text-sm font-medium text-blue">{j.n}</span>
                <p className="mt-3 text-lg font-medium text-ink">
                  {t(j.title)}
                </p>
                <p className="mt-1 text-base text-slate">{t(j.body)}</p>
              </Link>
            </li>
          ))}
        </ol>
        <div className="mt-14 grid gap-5 md:grid-cols-3 lg:grid-cols-4">
          {services.map((s) => (
            <Link
              key={s.slug}
              to={`/services/${s.slug}`}
              className="flex items-center gap-3 rounded-r8 bg-mist p-4 text-base font-medium text-ink hover:text-blue"
            >
              <Icon name={s.icon} className="size-5 text-blue" />
              {t(s.title)}
            </Link>
          ))}
        </div>
      </Section>
      <CTASection />
    </>
  )
}

export function Insights() {
  const t = useT()
  const { route } = useRouter()
  const [cat, setCat] = useState<string>("all")
  const list = publishedArticles().filter(
    (a) => cat === "all" || a.category === cat,
  )
  const fmt = new Intl.DateTimeFormat(
    route.locale === "ar" ? "ar-EG" : "en-GB",
    { dateStyle: "long" },
  )
  return (
    <>
      <PageHeader
        crumbs={[{ label: t({ en: "Insights", ar: "رؤى" }) }]}
        eyebrow={t({ en: "Knowledge center", ar: "مركز المعرفة" })}
        title={t({
          en: "Practical notes on building connected businesses.",
          ar: "ملاحظات عملية لبناء أعمال مترابطة.",
        })}
        lead={t({
          en: "Articles are reviewed by our team before publication.",
          ar: "تُراجع المقالات من قبل فريقنا قبل النشر.",
        })}
      />
      <Section>
        <div
          role="group"
          aria-label={t({
            en: "Filter by category",
            ar: "التصفية حسب التصنيف",
          })}
          className="flex flex-wrap gap-2"
        >
          {[
            { slug: "all", label: { en: "All", ar: "الكل" } },
            ...insightCategories,
          ].map((c) => (
            <button
              key={c.slug}
              type="button"
              aria-pressed={cat === c.slug}
              onClick={() => setCat(c.slug)}
              className={`min-h-10 rounded-full px-4 text-base font-medium ring-1 transition-colors ${
                cat === c.slug
                  ? "bg-blue text-white ring-blue"
                  : "text-ink ring-line hover:ring-blue"
              }`}
            >
              {t(c.label)}
            </button>
          ))}
        </div>
        <div className="mt-12">
          {list.length ? (
            <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
              {list.map((a) => (
                <LinkCard
                  key={a.slug}
                  to={`/insights/${a.slug}`}
                  eyebrow={fmt.format(new Date(a.publishedAt))}
                  title={t(a.title)}
                  body={t(a.excerpt)}
                />
              ))}
            </div>
          ) : (
            <EmptyState
              title={t({
                en: "No published articles yet",
                ar: "لا توجد مقالات منشورة بعد",
              })}
              body={t({
                en: "Our first articles are in editorial review. In the meantime, our service pages explain how we approach each area.",
                ar: "مقالاتنا الأولى قيد المراجعة التحريرية. وحتى ذلك الحين، تشرح صفحات الخدمات منهجنا في كل مجال.",
              })}
              action={
                <Button to="/services" variant="secondary">
                  {t({ en: "Explore services", ar: "استكشف الخدمات" })}
                </Button>
              }
            />
          )}
        </div>
      </Section>
    </>
  )
}

export function ArticleDetail({ slug }: { slug: string }) {
  const t = useT()
  const { route } = useRouter()
  const a = articles.find((x) => x.slug === slug && x.status === "published")!
  const related = publishedArticles()
    .filter((x) => x.category === a.category && x.slug !== a.slug)
    .slice(0, 3)
  const fmt = new Intl.DateTimeFormat(
    route.locale === "ar" ? "ar-EG" : "en-GB",
    { dateStyle: "long" },
  )
  return (
    <>
      <PageHeader
        crumbs={[
          { label: t({ en: "Insights", ar: "رؤى" }), to: "/insights" },
          { label: t(a.title) },
        ]}
        title={t(a.title)}
        lead={`${a.author} · ${fmt.format(new Date(a.publishedAt))}`}
      />
      <Section>
        <article className="mx-auto max-w-3xl space-y-6 text-lg leading-8 text-ink">
          {a.body.map((p, i) => (
            <p key={i}>{t(p)}</p>
          ))}
        </article>
      </Section>
      {related.length > 0 && (
        <Section tone="mist">
          <SectionHeading
            title={t({ en: "Related articles", ar: "مقالات ذات صلة" })}
          />
          <div className="mt-10 grid gap-5 md:grid-cols-3">
            {related.map((r) => (
              <LinkCard
                key={r.slug}
                to={`/insights/${r.slug}`}
                title={t(r.title)}
                body={t(r.excerpt)}
              />
            ))}
          </div>
        </Section>
      )}
    </>
  )
}

export function Privacy() {
  const t = useT()
  const items = [
    {
      h: { en: "What we collect", ar: "ما نجمعه" },
      p: {
        en: "When you submit a form we receive the details you enter — such as your name, company, email, phone number and project description — together with the page you submitted from.",
        ar: "عند إرسال نموذج نتلقى البيانات التي تدخلها، مثل الاسم والشركة والبريد الإلكتروني ورقم الهاتف ووصف المشروع، مع الصفحة التي أرسلت منها.",
      },
    },
    {
      h: { en: "How we use it", ar: "كيف نستخدمها" },
      p: {
        en: "We use this information only to respond to your inquiry and manage our business relationship with you. It is stored in our customer relationship management system.",
        ar: "نستخدم هذه المعلومات فقط للرد على استفسارك وإدارة علاقتنا التجارية معك، وتُحفظ في نظام إدارة علاقات العملاء لدينا.",
      },
    },
    {
      h: { en: "Spam protection", ar: "الحماية من الرسائل الآلية" },
      p: {
        en: "We use Google reCAPTCHA on inquiry forms to help detect automated submissions. Google processes browser and interaction signals for this verification under its Privacy Policy and Terms of Service, linked beside the form.",
        ar: "نستخدم Google reCAPTCHA في نماذج الاستفسار للمساعدة على اكتشاف الطلبات الآلية. تعالج Google إشارات المتصفح والتفاعل لإجراء هذا التحقق وفق سياسة الخصوصية وشروط الخدمة، وتجد روابطهما بجوار النموذج.",
      },
    },
    {
      h: { en: "Your choices", ar: "خياراتك" },
      p: {
        en: `You can ask us to access, correct or delete your information at any time by emailing ${SITE.email}.`,
        ar: `يمكنك طلب الاطلاع على معلوماتك أو تصحيحها أو حذفها في أي وقت عبر البريد ${SITE.email}.`,
      },
    },
  ]
  return (
    <>
      <PageHeader
        crumbs={[{ label: t({ en: "Privacy notice", ar: "إشعار الخصوصية" }) }]}
        title={t({ en: "Privacy notice", ar: "إشعار الخصوصية" })}
        lead={t({
          en: "A summary of how we handle information submitted through this website. A full legal policy will be published after legal review.",
          ar: "ملخص لكيفية تعاملنا مع المعلومات المرسلة عبر هذا الموقع. ستُنشر سياسة قانونية كاملة بعد المراجعة القانونية.",
        })}
      />
      <Section>
        <div className="max-w-3xl space-y-10">
          {items.map((i) => (
            <div key={i.h.en}>
              <h2 className="text-2xl font-medium text-ink">{t(i.h)}</h2>
              <p className="mt-3 text-lg leading-8 text-slate">{t(i.p)}</p>
            </div>
          ))}
        </div>
      </Section>
    </>
  )
}

export function NotFound() {
  const t = useT()
  return (
    <Container className="py-32">
      <EmptyState
        title={t({
          en: "We couldn\u2019t find that page.",
          ar: "لم نجد هذه الصفحة.",
        })}
        body={t({
          en: "The link may be outdated. Use the navigation or return home.",
          ar: "ربما يكون الرابط قديمًا. استخدم القائمة أو عد إلى الرئيسية.",
        })}
        action={
          <Button to="/">
            {t({ en: "Back to home", ar: "العودة إلى الرئيسية" })}
          </Button>
        }
      />
    </Container>
  )
}
